import { Link } from "react-router-dom";
import { Check, ArrowRight, Zap } from "lucide-react";
import PricingCalculator from "../components/PricingCalculator";
import { inr, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useCurrency } from "../context/CurrencyContext";

const PREPAID_STEPS = ["REQUIREMENT", "ESTIMATE", "APPROVAL", "PAYMENT", "DEVELOPMENT", "HANDOVER"];

export default function Pricing() {
  const { settings } = useSite();
  const { money, currency } = useCurrency();
  const r = settings.rates;

  const models = [
    {
      key: "standard", name: "STANDARD DELIVERY", rate: r.standard, tag: "Built in days",
      points: ["Suitable applications targeted for ~7-day delivery", "Many projects targeted within ~50 development hours", "Full analysis, build, testing, deployment & handover"],
      example: `50 hours × ${inr(r.standard)} = ${inr(50 * r.standard)}`,
    },
    {
      key: "specialist", name: "VIBE CODING SPECIALIST", rate: r.specialist, tag: "Rush Hour — live",
      points: ["Live build session with your stakeholders", "Ideal for MVPs, dashboards, portals, tools", "Many suitable apps in ~3–5 hour sessions"],
      example: `4 hours × ${inr(r.specialist)} = ${inr(4 * r.specialist)}`,
    },
    {
      key: "super", name: "SUPER VIBE CODER", rate: r.super, tag: "Rush Hour — senior",
      points: ["Critical & high-priority applications", "Executive stakeholder sessions", "Complex MVPs and enterprise prototypes"],
      example: `4 hours × ${inr(r.super)} = ${inr(4 * r.super)}`,
    },
  ];

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="pricing-page">
      <div className="container-x">
        <p className="section-label">PRICING</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white max-w-3xl">
          TRANSPARENT. HOURLY. <span className="text-flame">PREPAID.</span>
        </h1>
        <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
          Hourly rates are fixed by delivery model. Final project cost depends on estimated development hours and
          approved scope — confirmed in writing before any work begins.
        </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {models.map((m) => (
            <div key={m.key} data-testid={`pricing-card-${m.key}`} className={`rounded-2xl p-8 sm:p-10 card-lift ${m.key === "super" ? "bg-navy-800 tracing-beam" : "border border-white/10 bg-navy-800"}`}>
              <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400">{m.tag.toUpperCase()}</p>
              <h2 className="mt-3 font-heading text-xl font-bold text-white">{m.name}</h2>
              <p className="mt-6 font-heading text-4xl font-black text-white" data-testid={`pricing-rate-${m.key}`}>
                {money(m.rate)}<span className="text-sm font-semibold text-slate-400">/hour</span>
              </p>
              {currency !== "INR" && (
                <p className="mt-1.5 text-[10px] font-mono text-slate-500">Equivalent of INR base pricing at current exchange rate.</p>
              )}
              <ul className="mt-7 space-y-3">
                {m.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed">
                    <Check className="w-4 h-4 text-flame shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <p className="mt-7 rounded-lg bg-navy-900/70 border border-white/5 p-3.5 font-mono text-[11px] text-slate-400">{m.example}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-slate-600">Examples are illustrative. Final pricing and timeline are confirmed after requirement analysis.</p>

        <div className="mt-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
            RUN YOUR OWN <span className="text-flame">NUMBERS.</span>
          </h2>
          <div className="mt-10">
            <PricingCalculator />
          </div>
        </div>

        {/* Prepaid model */}
        <div className="mt-24 rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-14" data-testid="prepaid-model-section">
          <p className="section-label">THE PREPAID MODEL</p>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">NO OPEN-ENDED DEVELOPMENT.</h2>
          <p className="mt-4 text-sm text-slate-400 max-w-2xl leading-relaxed">
            All development engagements are prepaid. Development begins after you approve the scope and estimate and
            complete payment. Requirements outside the approved scope are estimated separately and require approval and
            payment before development.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-y-3">
            {PREPAID_STEPS.map((s, i) => (
              <div key={s} className="flex items-center">
                <span className="rounded-full border border-flame/40 bg-flame/10 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.15em] text-flame">{s}</span>
                {i < PREPAID_STEPS.length - 1 && <ArrowRight className="w-4 h-4 text-slate-600 mx-2 shrink-0" />}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-col sm:flex-row gap-4">
          <Link to="/get-estimate" data-testid="pricing-estimate-btn" onClick={() => track("estimate_click", { location: "pricing" })} className="inline-flex items-center justify-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold px-8 py-4 transition-colors">
            GET MY ESTIMATE <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/book-rush-hour" data-testid="pricing-rush-btn" onClick={() => track("rush_hour_click", { location: "pricing" })} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 hover:border-flame hover:text-flame text-white font-heading font-bold px-8 py-4 transition-colors">
            <Zap className="w-4 h-4" /> BOOK RUSH HOUR
          </Link>
        </div>
      </div>
    </div>
  );
}
